import {
  dedupeJobTitleValues,
  fetchAllJobTitles,
  mapJobTitleValues,
} from './job-title-options.shared';
import { OnOptionsLoad } from './types';

/** Synthetic autocomplete value; stripped before API requests. */
export const ALL_SCHOOLS_JOB_TITLE_ID = -3;

export const ALL_SCHOOLS_JOB_TITLE_OPTION: {
  id: number;
  name: string;
} = {
  id: ALL_SCHOOLS_JOB_TITLE_ID,
  name: 'All Schools',
};

/**
 * School / K–12 job title IDs for "All Schools".
 * Excludes college/university roles (professor, college athletes, etc.).
 */
export const SCHOOLS_JOB_TITLE_IDS = [
  5, // Teacher
  11, // Coach
  19, // School Bus Driver
  50, // House Parent
  52, // Head of Discipline
  53, // School Principal/Headmaster/Headmistress
  61, // School counselor
  62, // High School Campus Supervisor/Security
] as const;

export const SCHOOLS_JOB_TITLE_NAMES = [
  'Teacher',
  'Coach',
  'School Bus Driver',
  'House Parent',
  'Head of Discipline',
  'School Principal/Headmaster/Headmistriss',
  'School Principal/Headmaster/Headmistress',
  'School counselor',
  'High School Campus Supervisor/Security',
] as const;

const normalizedSchoolsJobTitleIds = new Set<number>(SCHOOLS_JOB_TITLE_IDS);

const normalizedSchoolsJobTitleNames = new Set(
  SCHOOLS_JOB_TITLE_NAMES.map((name) => name.trim().toLowerCase()),
);

export const isAllSchoolsJobTitleOption = (
  item: { id?: number | string } | null | undefined,
): boolean => Number(item?.id) === ALL_SCHOOLS_JOB_TITLE_ID;

export const isSchoolsJobTitle = (jobTitle: {
  id?: number | string;
  name?: string;
}): boolean => {
  const id = Number(jobTitle.id);

  if (Number.isFinite(id) && normalizedSchoolsJobTitleIds.has(id)) {
    return true;
  }

  if (!jobTitle.name) {
    return false;
  }

  return normalizedSchoolsJobTitleNames.has(jobTitle.name.trim().toLowerCase());
};

export async function loadSchoolsJobTitles(
  onOptionsLoad: OnOptionsLoad,
): Promise<Array<{ id: number; name: string }>> {
  const allJobTitles = await fetchAllJobTitles(onOptionsLoad);

  return mapJobTitleValues(
    allJobTitles.filter((jobTitle) => isSchoolsJobTitle(jobTitle)),
  );
}

export async function resolveAllSchoolsJobTitles(
  onOptionsLoad: OnOptionsLoad,
  currentValue: Array<{ id: number; name: string }> | undefined,
): Promise<Array<{ id: number; name: string }>> {
  const schoolsJobTitles = await loadSchoolsJobTitles(onOptionsLoad);

  const withoutAllSchools = (currentValue ?? [])
    .filter((item) => !isAllSchoolsJobTitleOption(item))
    .map((item) => ({
      id: typeof item.id === 'number' ? item.id : Number(item.id),
      name: item.name ?? '',
    }));

  return dedupeJobTitleValues([...withoutAllSchools, ...schoolsJobTitles]);
}
